import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Play, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const TRIGGERS = [
  { value: "welcome", label: "Welcome (new signup)" },
  { value: "abandoned_cart", label: "Abandoned cart" },
  { value: "post_purchase", label: "Post-purchase" },
  { value: "occasion", label: "Occasion (birthday / anniversary)" },
  { value: "manual", label: "Manual enrollment only" },
];
const CHANNELS = ["whatsapp", "email", "sms"];
const EMPTY = { name: "", trigger: "manual", occasion_lead_days: 7, active: false, steps: [] };

export default function Journeys() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null); // draft being edited
  const [runs, setRuns] = useState([]);
  const [sends, setSends] = useState([]);
  const [enrollEmail, setEnrollEmail] = useState("");

  const load = () => api.get("/admin/crm/journeys").then((r) => setList(r.data)).catch(() => {});
  const loadSends = () => api.get("/admin/crm/sends", { params: { limit: 50 } }).then((r) => setSends(r.data)).catch(() => {});
  const loadRuns = (id) => api.get(`/admin/crm/journeys/${id}/runs`).then((r) => setRuns(r.data)).catch(() => setRuns([]));
  const [cap, setCap] = useState("");
  useEffect(() => {
    load();
    loadSends();
    api.get("/admin/settings").then((r) => setCap(String(r.data.journey_max_sends_per_day ?? 3))).catch(() => {});
  }, []);

  const saveCap = async () => {
    try {
      await api.put("/admin/settings", { journey_max_sends_per_day: Number(cap) || 0 });
      toast.success("Daily send cap saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const select = (j) => { setSel({ ...j }); setEnrollEmail(""); if (j.id) loadRuns(j.id); else setRuns([]); };

  const save = async () => {
    try {
      const r = sel.id
        ? await api.patch(`/admin/crm/journeys/${sel.id}`, sel)
        : await api.post("/admin/crm/journeys", sel);
      toast.success("Journey saved");
      setSel({ ...r.data });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async () => {
    if (!sel?.id || !window.confirm(`Delete journey "${sel.name}"? Active runs will be stopped.`)) return;
    await api.delete(`/admin/crm/journeys/${sel.id}`).catch((e) => toast.error(errMsg(e)));
    setSel(null);
    load();
  };

  const enroll = async () => {
    if (!enrollEmail.trim()) return;
    try {
      await api.post(`/admin/crm/journeys/${sel.id}/enroll`, { email: enrollEmail.trim() });
      toast.success(`Enrolled ${enrollEmail}`);
      setEnrollEmail("");
      loadRuns(sel.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runEngine = async () => {
    try {
      const r = await api.post("/admin/crm/engine/run");
      toast.success(`Engine ran — ${r.data.enrolled} enrolled, ${r.data.sent} sends processed${r.data.capped ? `, ${r.data.capped} deferred (daily cap)` : ""}`);
      loadSends();
      if (sel?.id) loadRuns(sel.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setStep = (i, patch) => setSel((s) => ({ ...s, steps: s.steps.map((st, j) => (j === i ? { ...st, ...patch } : st)) }));
  const moveStep = (i, dir) => setSel((s) => {
    const steps = [...s.steps];
    const j = i + dir;
    if (j < 0 || j >= steps.length) return s;
    [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...s, steps };
  });

  return (
    <div className="space-y-4" data-testid="journeys-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Journeys</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runEngine} data-testid="run-engine-btn"><Play size={13} className="mr-1" /> Run engine now</Button>
          <Button size="sm" className="bg-neutral-900 text-white" onClick={() => select({ ...EMPTY })} data-testid="new-journey-btn"><Plus size={13} className="mr-1" /> New journey</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-neutral-500" data-testid="journey-cap-row">
        <span>Daily send cap per customer</span>
        <Input value={cap} onChange={(e) => setCap(e.target.value)} className="h-8 w-16 bg-white text-xs" data-testid="journey-cap-input" />
        <Button size="sm" variant="outline" onClick={saveCap} data-testid="journey-cap-save">Save</Button>
        <span className="text-neutral-400">0 = no cap · sends beyond the cap defer to the next day</span>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100 self-start" data-testid="journey-list">
          {list.map((j) => (
            <button key={j.id} onClick={() => select(j)}
              className={`w-full text-left p-3 text-sm hover:bg-neutral-50 ${sel?.id === j.id ? "bg-brand-light/40 border-l-2 border-brand" : ""}`}
              data-testid={`journey-item-${j.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium">{j.name}</span>
                <span className={`text-[10px] font-mono uppercase ${j.active ? "text-emerald-600" : "text-neutral-400"}`}>{j.active ? "Active" : "Off"}</span>
              </div>
              <p className="text-xs text-neutral-400 mt-0.5">{j.trigger} · {j.steps?.length || 0} steps · {j.run_count} runs</p>
            </button>
          ))}
          {!list.length && <p className="p-4 text-xs text-neutral-400" data-testid="no-journeys">No journeys yet.</p>}
        </div>

        <div className="md:col-span-2 space-y-4">
          {!sel ? (
            <div className="bg-white border border-neutral-200 p-8 text-center text-sm text-neutral-400" data-testid="journey-empty">
              Select a journey or create a new one.
            </div>
          ) : (
            <div className="bg-white border border-neutral-200 p-4 space-y-4" data-testid="journey-editor">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} className="bg-white" data-testid="journey-name-input" />
                </div>
                <div>
                  <Label>Trigger</Label>
                  <select value={sel.trigger} onChange={(e) => setSel({ ...sel, trigger: e.target.value })}
                    className="w-full h-9 border border-neutral-300 bg-white px-3 text-sm" data-testid="journey-trigger-select">
                    {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              {sel.trigger === "occasion" && (
                <div>
                  <Label>Start this many days before the occasion</Label>
                  <Input type="number" min="0" value={sel.occasion_lead_days}
                    onChange={(e) => setSel({ ...sel, occasion_lead_days: Number(e.target.value) || 0 })}
                    className="bg-white w-32" data-testid="journey-lead-days" />
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={sel.active} onCheckedChange={(v) => setSel({ ...sel, active: v })} data-testid="journey-active-switch" />
                Journey active (engine enrolls + sends)
              </label>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Steps</p>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSel((s) => ({ ...s, steps: [...s.steps, { delay_hours: 24, channel: "email", template: "Hi {{name}}!" }] }))} data-testid="journey-add-step-btn">
                    + Add Step
                  </Button>
                </div>
                {/* Visual canvas: trigger → steps as a vertical flow */}
                <div className="border border-neutral-200 bg-neutral-50/60 p-4 mb-3 overflow-x-auto rounded-lg" data-testid="journey-canvas">
                  <div className="flex flex-col items-center min-w-52">
                    <div className="px-4 py-2 bg-brand text-ivory text-xs font-mono uppercase tracking-wider rounded-full shadow-sm" data-testid="canvas-trigger">
                      ⚡ {sel.trigger === "occasion" ? `occasion (T−${sel.occasion_lead_days}d)` : sel.trigger.replace(/_/g, " ")}
                    </div>
                    {sel.steps.map((st, i) => (
                      <div key={i} className="flex flex-col items-center w-full">
                        <div className="w-px h-4 bg-neutral-300" />
                        <div className="px-2 py-0.5 text-[10px] font-mono text-neutral-400 bg-white border border-neutral-200 rounded-full shadow-2xs">
                          +{st.delay_hours}h delay
                        </div>
                        <div className="w-px h-4 bg-neutral-300" />
                        <div
                          className="w-full max-w-sm border border-neutral-200 bg-white p-3 text-left hover:border-brand transition-colors rounded shadow-xs cursor-pointer group"
                          onClick={() => document.querySelector(`[data-testid="journey-step-${i}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                          data-testid={`canvas-node-${i}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-mono uppercase tracking-wider text-brand font-bold">{st.channel}</span>
                            <span className="text-[10px] font-mono text-neutral-400">Step #{i + 1}</span>
                          </div>
                          <p className="text-xs text-neutral-600 mt-1 line-clamp-2">{st.template || <em className="text-neutral-300">empty template — click to edit below</em>}</p>
                        </div>
                      </div>
                    ))}
                    {!sel.steps.length && <p className="text-xs text-neutral-400 mt-3">No steps — click "+ Add Step" above.</p>}
                  </div>
                </div>
                <div className="space-y-2">
                  {sel.steps.map((st, i) => (
                    <div key={i} className="border border-neutral-200 p-3 space-y-2" data-testid={`journey-step-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-mono text-neutral-400">#{i + 1}</span>
                        <span className="text-xs">wait</span>
                        <Input type="number" min="0" value={st.delay_hours}
                          onChange={(e) => setStep(i, { delay_hours: Number(e.target.value) || 0 })}
                          className="h-8 w-20 bg-white text-xs" data-testid={`step-delay-${i}`} />
                        <span className="text-xs">hours, then</span>
                        <select value={st.channel} onChange={(e) => setStep(i, { channel: e.target.value })}
                          className="h-8 border border-neutral-300 bg-white px-2 text-xs" data-testid={`step-channel-${i}`}>
                          {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <div className="ml-auto flex gap-1">
                          <button onClick={() => moveStep(i, -1)} className="p-1 hover:bg-neutral-100" data-testid={`step-up-${i}`}><ArrowUp size={13} /></button>
                          <button onClick={() => moveStep(i, 1)} className="p-1 hover:bg-neutral-100" data-testid={`step-down-${i}`}><ArrowDown size={13} /></button>
                          <button onClick={() => setSel((s) => ({ ...s, steps: s.steps.filter((_, j) => j !== i) }))}
                            className="p-1 hover:bg-neutral-100 text-red-500" data-testid={`step-remove-${i}`}><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <textarea value={st.template} onChange={(e) => setStep(i, { template: e.target.value })}
                        rows={2} placeholder="Hi {{name}}, your cart is waiting: {{items}}…"
                        className="w-full border border-neutral-300 bg-white px-2 py-1.5 text-xs" data-testid={`step-template-${i}`} />
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSel((s) => ({ ...s, steps: [...s.steps, { delay_hours: 0, channel: "whatsapp", template: "" }] }))}
                    data-testid="add-step-btn"><Plus size={13} className="mr-1" /> Add step</Button>
                  <p className="text-[11px] text-neutral-400">Variables: {"{{name}} {{items}} {{value}} {{order_number}} {{occasion}}"} · Sends use mock adapters until provider keys are configured.</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={save} className="bg-neutral-900 text-white" data-testid="save-journey-btn">Save journey</Button>
                {sel.id && <Button variant="outline" className="text-red-600 border-red-200" onClick={del} data-testid="delete-journey-btn">Delete</Button>}
              </div>

              {sel.id && (
                <div className="border-t border-neutral-100 pt-3">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Manual enroll</p>
                  <div className="flex gap-1">
                    <Input value={enrollEmail} onChange={(e) => setEnrollEmail(e.target.value)} placeholder="customer@email.com" className="h-8 text-xs bg-white" data-testid="enroll-email-input" />
                    <Button size="sm" variant="outline" onClick={enroll} data-testid="enroll-btn">Enroll</Button>
                  </div>
                  {!!runs.length && (
                    <div className="mt-3">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Recent runs</p>
                      <div className="divide-y divide-neutral-100 max-h-48 overflow-y-auto">
                        {runs.map((r) => (
                          <div key={r.id} className="py-1.5 text-xs flex justify-between" data-testid={`journey-run-${r.id}`}>
                            <span>{r.customer_email}</span>
                            <span className="text-neutral-400">step {r.step_idx + 1} · {r.status}</span>
                            <span className="text-neutral-400">{fmtDateTime(r.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="bg-white border border-neutral-200" data-testid="send-log">
            <div className="p-3 border-b border-neutral-100">
              <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Send log (mock adapters)</p>
            </div>
            <div className="divide-y divide-neutral-100 max-h-64 overflow-y-auto">
              {sends.map((s) => (
                <div key={s.id} className="p-3 text-xs" data-testid={`send-${s.id}`}>
                  <div className="flex justify-between gap-2">
                    <span className="font-mono uppercase">{s.channel} → {s.to || s.customer_email}</span>
                    <span className={`font-mono ${s.status === "mock_sent" ? "text-emerald-600" : "text-amber-600"}`}>{s.status}</span>
                  </div>
                  <p className="text-neutral-500 mt-0.5">{s.body}</p>
                  <p className="text-neutral-400">{fmtDateTime(s.created_at)}</p>
                </div>
              ))}
              {!sends.length && <p className="p-4 text-xs text-neutral-400" data-testid="no-sends">No sends yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
